/**
 * Artifacts — file-kind collaboration output (task 828).
 *
 * Report: `project/reports/innovate-service-collaboration-artifact-outputs.md`.
 *
 * An **artifact** is the identity that survives revision; an **artifact
 * version** is one immutable revision of its content, stamped with the
 * collaboration that produced it. Output with a typed home of its own — an AI
 * Service, a test, a generated interface — stays there; everything else a
 * collaboration writes lands here.
 *
 * These shapes mirror what the service actually returns (`innovate-service`
 * `src/types/domain/artifact.ts`, `src/processes/artifact.ts`), so the app never
 * re-labels what the service already names. Two details of that contract shape
 * everything below:
 *
 *   1. **A list row carries its latest version and no content.** The service
 *      includes `latestVersion` on every row precisely so a list needs no second
 *      request, and content bytes never ride in a JSON response — the only way
 *      to them is the download endpoint.
 *   2. **Versions are addressed by their 1-based NUMBER**, not their id. That is
 *      what a turn output carries and what the download URL takes.
 */

/** The kinds the service's `ArtifactKind` enum declares. */
export const ARTIFACT_KINDS = ['DOCUMENT', 'FILE', 'IMAGE', 'DATASET'] as const;

export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

/** Where a version's bytes live. Only `DATABASE` is written in v1 (task 825). */
export type ContentStorage = 'DATABASE' | 'OBJECT_STORE';

/**
 * One version's metadata, as the service selects it. No content: the bytes are
 * reachable only through the download endpoint.
 */
export interface ArtifactVersionSummary {
  id: string;
  versionNumber: number;
  sizeBytes: number;
  contentStorage: ContentStorage;
  /** Provenance — the collaboration run that produced this version. */
  collaborationId: string;
  journalEntryId: string | null;
  createdAt: string;
}

/** An artifact, with the metadata of its newest version. */
export interface Artifact {
  id: string;
  accountId?: string;
  /** The resource this artifact belongs to, when it was produced for one. */
  resourceId: string | null;
  kind: ArtifactKind;
  /** The filename a download saves as, e.g. `usage-summary-2026-08.md`. */
  name: string;
  contentType: string;
  /** How many versions exist — versions are numbered 1…this. */
  currentVersionNumber: number;
  createdAt: string;
  updatedAt?: string;
  /** Null only for an artifact whose versions were all removed. */
  latestVersion: ArtifactVersionSummary | null;
}

/** `GET /artifacts` — both lenses (account-wide, and per resource) answer this. */
export interface ArtifactListResponse {
  artifacts: Artifact[];
  total: number;
}

/** `GET /artifacts/:id/versions/:n` — one version, plus the artifact it belongs to. */
export interface ArtifactVersionDetail extends ArtifactVersionSummary {
  artifactId: string;
  artifact: Pick<Artifact, 'id' | 'name' | 'contentType' | 'kind'>;
}

export interface ArtifactFilters {
  kind?: ArtifactKind;
  resourceId?: string;
}

/**
 * The reference a collaboration's turn output carries (task 826, report §P7).
 *
 * Deliberately not the artifact row: a transcript POINTS AT an artifact, it does
 * not re-store it, so there are no bytes here and nothing that goes stale except
 * the name. Everything a card needs to render is in it, which is why a card
 * costs no request.
 */
export interface ArtifactReference {
  id: string;
  versionId: string;
  versionNumber: number;
  name: string;
  kind: ArtifactKind;
  contentType: string;
  sizeBytes: number;
}

/**
 * The base an artifact is revised from, as the turn INPUT carries it.
 *
 * The engine's save step reads `baseArtifact` off the turn input (task 826) and
 * appends the next version to that artifact instead of opening a new one. The
 * key is the same one the UI tier already uses for its own base — one vocabulary
 * for "revise the thing I am pointing at", whatever kind of thing it is.
 */
export interface BaseArtifactInput {
  artifactId: string;
  versionNumber?: number;
  name?: string;
}

/** Is this value shaped like a turn output's artifact reference? */
export const isArtifactReference = (value: unknown): value is ArtifactReference => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.versionId === 'string' &&
    typeof candidate.versionNumber === 'number' &&
    typeof candidate.name === 'string' &&
    typeof candidate.contentType === 'string' &&
    (ARTIFACT_KINDS as readonly string[]).includes(String(candidate.kind))
  );
};

/**
 * Every artifact reference in one turn output, in the order the output lists
 * them, each appearing once.
 *
 * **Why a search and not a lookup at `output.artifact`.** The save step is a
 * registered function node (task 826), so its result reaches the turn output
 * under whatever key the collaboration's `outputMapping` gives it — usually the
 * node's own id — and the node returns `{ saved, artifact }` rather than the
 * reference bare. A renderer that only read `output.artifact` would show raw
 * JSON for every definition that did not happen to name its mapping row
 * `artifact`. So this recognises a reference by its SHAPE, at the top level and
 * one level inside a save result, and by nothing else.
 */
export const collectArtifactReferences = (output: unknown): ArtifactReference[] => {
  if (!output || typeof output !== 'object') return [];

  const found: ArtifactReference[] = [];
  const seen = new Set<string>();

  const take = (candidate: unknown) => {
    if (!isArtifactReference(candidate) || seen.has(candidate.versionId)) return;
    seen.add(candidate.versionId);
    found.push(candidate);
  };

  for (const value of Object.values(output as Record<string, unknown>)) {
    take(value);
    if (value && typeof value === 'object') {
      // `{ saved: true, artifact: {...} }` — what the save step itself returns.
      take((value as Record<string, unknown>).artifact);
    }
  }

  return found;
};

/** `1 kB` / `2.4 MB` — sizes are metadata, so they read as approximations. */
export const formatArtifactSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} kB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};
